import { Navigate } from 'react-router-dom';
import { usePermissions } from '@/contexts/PermissionContext';
import { hasSelfServicePermissionsOnly } from '@/utils/selfServicePermissions';
import CompanyDashboard from '@/pages/company/CompanyDashboard';
import { Skeleton } from '@/components/ui/skeleton';

const DashboardLoading = () => (
  <div className="space-y-6" data-testid="dashboard-route-loading">
    <Skeleton className="h-64 w-full rounded-xl" />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-32 rounded-xl" />
      ))}
    </div>
  </div>
);

/**
 * Shared dashboard route for company_admin and elevated employees.
 * Self-service-only employees are redirected to /profile (their dashboard).
 */
const DashboardRoute = ({ user, onLogout }) => {
  const { permissions, isAdmin, loading: permissionsLoading } = usePermissions();

  const isSelfServiceOnly = hasSelfServicePermissionsOnly({
    permissions,
    isAdmin,
    isCompanyAdmin: user?.role === 'company_admin',
    isSuperadmin: user?.role === 'superadmin',
    permissionsLoading,
    modulesLoading: false,
  });

  // Only block when we have no permissions yet (login seeds permissions immediately)
  const awaitingPermissions = permissionsLoading && permissions.length === 0;

  if (awaitingPermissions) {
    return <DashboardLoading />;
  }

  if (user?.role === 'employee' && isSelfServiceOnly) {
    return <Navigate to="/profile" replace />;
  }

  return <CompanyDashboard user={user} onLogout={onLogout} />;
};

export default DashboardRoute;
